// Selector de voz para el modo "Video con influencer" (V2).
//
// Mismo chrome que `GanchoPicker` (portal: bottom sheet en mobile, panel
// lateral derecho en desktop). Lista UNIFICADA (sin pestañas, ADR-531):
//   • "Clonar voz" es un ÍTEM de la lista, con card del mismo tamaño que las
//     de voz (dos líneas + icono a la derecha).
//   • SIN opción "Automática": la voz es OBLIGATORIA (ADR-544) — el padre
//     valida `vozId != null` antes de generar y preselecciona la última voz
//     usada.
//   • Las voces en INGLÉS del catálogo se ocultan (ADR-544).
//   • Si hay voces clonadas: título "Mis voces" + esas voces + card
//     "Clonar voz"; si no, la card "Clonar voz" va primera.
//   • Después SIEMPRE: subtítulo "Voces de Atlas" + filtros de género +
//     el catálogo.
//   • Catálogo (tabla `voces`): preview reproducible + filtro por género.
//     Las clonadas se seleccionan por `elevenlabs_voice_id` (como el editor).
//   • "Clonar voz" abre el `VoiceCloneCreator` (mismo flujo del editor) como
//     sub-vista del panel; al crear, la voz se agrega y queda seleccionada.
//
// OJO clases: los colores brand-* NO soportan modificador de opacidad (el
// config los define como var() sin <alpha-value> — la clase no compila).
// Para tintes usar hex arbitrario: `bg-[#1644EB]/[0.06]`, `border-[#1644EB]/40`.

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, Loader2, Mic, Pause, Play, Plus, X } from 'lucide-react';
import { useExitTransition } from '../../../hooks/useExitTransition';
import { fetchVoices, type VoiceV2 } from '../voicesApi';
import {
  fetchClonedVoices,
  type ClonedVoice,
} from '../../../lib/clonedVoicesApi';
import VoiceCloneCreator from '../../../components/VoiceCloneCreator';

export interface VoicePickerV2Props {
  open: boolean;
  onClose: () => void;
  /** Usuario logueado — requerido por el flujo de clonado de voz. */
  userId?: string | null;
  /** Empresa activa — para listar las voces clonadas. */
  companyId: string | null;
  /** Voz elegida: `voces.id` (Atlas) o `elevenlabs_voice_id` (clonada).
   *  null = todavía no eligió (la voz es obligatoria, ADR-544). */
  selectedVozId: string | null;
  /** `nombre` viaja junto al id para mostrarlo en la fila "Voz" del panel. */
  onSelect: (vozId: string, nombre: string) => void;
}

// Voces del catálogo que NO se ofrecen en este modo: los videos salen en
// español (ADR-544). Match laxo por si la columna dice "Inglés"/"English".
function isEnglishVoice(idioma: string | null): boolean {
  const v = (idioma ?? '').toLowerCase();
  return v.includes('ingl') || v.includes('english');
}

const SECTION_TITLE_CLASS =
  'text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55';

export const VoicePickerV2: React.FC<VoicePickerV2Props> = ({
  open,
  onClose,
  userId,
  companyId,
  selectedVozId,
  onSelect,
}) => {
  const { mounted, animated } = useExitTransition(open, 300);
  const [view, setView] = useState<'list' | 'create'>('list');
  const [atlasVoices, setAtlasVoices] = useState<VoiceV2[]>([]);
  const [clonedVoices, setClonedVoices] = useState<ClonedVoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [sexFilter, setSexFilter] = useState<string>('Todas');
  const fetchedRef = useRef(false);

  // Preview de voz: un solo <audio> compartido; `playingId` marca cuál suena.
  const previewRef = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);

  // Fetch lazy en la primera apertura (Atlas + clonadas en paralelo).
  useEffect(() => {
    if (!open || fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    void Promise.all([
      fetchVoices(),
      companyId ? fetchClonedVoices(companyId) : Promise.resolve([]),
    ])
      .then(([atlas, cloned]) => {
        setAtlasVoices(atlas);
        setClonedVoices(cloned);
        setLoading(false);
      })
      .catch((err) => {
        // Sin catch, un reject dejaba el spinner colgado para siempre y
        // `fetchedRef` en true impedía reintentar al reabrir. Reseteamos el
        // flag para que la próxima apertura vuelva a intentar el fetch.
        console.error('[VoicePickerV2] fetch de voces falló', err);
        fetchedRef.current = false;
        setLoading(false);
      });
  }, [open, companyId]);

  const stopPreview = () => {
    if (previewRef.current) {
      previewRef.current.pause();
      previewRef.current = null;
    }
    setPlayingId(null);
  };

  // Al cerrar el panel: cortar el preview y volver a la lista.
  useEffect(() => {
    if (!open) {
      stopPreview();
      setView('list');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const togglePreview = (voice: VoiceV2) => {
    if (!voice.previewUrl) return;
    if (playingId === voice.id) {
      stopPreview();
      return;
    }
    stopPreview();
    const audio = new Audio(voice.previewUrl);
    previewRef.current = audio;
    setPlayingId(voice.id);
    audio.onended = () => setPlayingId(null);
    void audio.play().catch(() => setPlayingId(null));
  };

  const pick = (vozId: string, nombre: string) => {
    stopPreview();
    onSelect(vozId, nombre);
    onClose();
  };

  if (!mounted) return null;

  // Catálogo sin voces en inglés (ADR-544); chips derivados del visible.
  const visibleAtlas = atlasVoices.filter((v) => !isEnglishVoice(v.idioma));
  const sexOptions = [
    'Todas',
    ...Array.from(
      new Set(visibleAtlas.map((v) => v.sexo).filter((s): s is string => !!s)),
    ).sort(),
  ];
  const filteredAtlas =
    sexFilter === 'Todas'
      ? visibleAtlas
      : visibleAtlas.filter((v) => v.sexo === sexFilter);
  const hasCloned = clonedVoices.length > 0;

  // Card "Clonar voz" — ítem de la lista, mismo tamaño que las cards de voz
  // (dos líneas + icono a la derecha); borde dashed como affordance de crear.
  const clonarVozButton = (
    <button
      type="button"
      onClick={() => setView('create')}
      disabled={!userId || !companyId}
      className="w-full text-left rounded-2xl p-3.5 bg-white border border-dashed border-[#1644EB]/40 hover:border-brand-primary transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <div className="flex-1 min-w-0">
        <span className="text-[14px] font-medium text-brand-primary">
          Clonar voz
        </span>
        <p className="text-[12px] text-[#3C3C43]/50 mt-0.5">
          Creá una copia de tu propia voz
        </p>
      </div>
      <Plus className="w-4 h-4 text-brand-primary shrink-0" />
    </button>
  );


  const filterChips = sexOptions.length > 2 && (
    <div className="flex gap-1.5 flex-wrap">
      {sexOptions.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => setSexFilter(opt)}
          className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium transition-colors ${
            sexFilter === opt
              ? 'bg-brand-primary text-white'
              : 'bg-black/[0.05] text-[#3C3C43]/60 hover:bg-black/[0.08]'
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );

  const atlasRows =
    filteredAtlas.length === 0 ? (
      <p className="text-[13.5px] text-[#3C3C43]/50 text-center py-10">
        No pudimos cargar las voces. Podés seguir con "Automática".
      </p>
    ) : (
      filteredAtlas.map((voice) => {
        const isSelected = selectedVozId === voice.id;
        const isPlaying = playingId === voice.id;
        return (
          <div
            key={voice.id}
            className={`w-full rounded-2xl p-3.5 transition-colors flex items-center gap-3 ${
              isSelected
                ? 'bg-[#1644EB]/[0.06] ring-2 ring-brand-primary'
                : 'bg-white ring-1 ring-black/[0.08] hover:bg-black/[0.02]'
            }`}
          >
            <button
              type="button"
              onClick={() => pick(voice.id, voice.nombre)}
              className="flex-1 min-w-0 text-left"
            >
              <div className="flex items-center gap-2">
                <span className="text-[14px] font-medium text-brand-black truncate">
                  {voice.nombre}
                </span>
                {isSelected && (
                  <Check className="w-3.5 h-3.5 text-brand-primary shrink-0" />
                )}
              </div>
              <p className="text-[12px] text-[#3C3C43]/50 mt-0.5 truncate">
                {[voice.sexo, voice.idioma, voice.acento]
                  .filter(Boolean)
                  .join(' · ') || voice.descripcion || 'Voz'}
              </p>
            </button>
            {voice.previewUrl && (
              <button
                type="button"
                onClick={() => togglePreview(voice)}
                className={`w-9 h-9 rounded-full inline-flex items-center justify-center shrink-0 transition-colors ${
                  isPlaying
                    ? 'bg-brand-primary text-white'
                    : 'bg-black/[0.05] text-[#3C3C43]/60 hover:bg-black/[0.1]'
                }`}
                aria-label={isPlaying ? 'Pausar preview' : 'Escuchar preview'}
              >
                {isPlaying ? (
                  <Pause className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4 ml-0.5" />
                )}
              </button>
            )}
          </div>
        );
      })
    );

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed z-[211] bg-white flex flex-col shadow-2xl transition-transform duration-300 ease-out
          inset-x-0 bottom-0 rounded-t-[13px] max-h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))]
          md:inset-x-auto md:left-auto md:top-4 md:right-4 md:bottom-4 md:h-auto md:w-[480px] md:rounded-[13px]
          ${
            animated
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-[calc(100%+32px)]'
          }`}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        {view === 'create' ? (
          // Sub-vista "Clonar voz" — mismo flujo del editor. Trae su propio
          // header con botón Volver.
          <div className="flex-1 min-h-0">
            <VoiceCloneCreator
              userId={userId ?? ''}
              companyId={companyId ?? ''}
              onCreated={(voice) => {
                setClonedVoices((prev) => [voice, ...prev]);
                // Auto-seleccionar la voz recién creada (queda marcada en la
                // lista; el user cierra cuando quiere).
                onSelect(voice.elevenlabsVoiceId, voice.nombre);
                setView('list');
              }}
              onBack={() => setView('list')}
            />
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 md:py-4 shrink-0">
              <h3 className="text-[16px] md:text-[17px] font-semibold text-brand-black md:flex-1">
                Voz
              </h3>
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-[#3C3C43]/40 hover:bg-black/[0.1] transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08] shrink-0" />

            {/* Lista unificada de voces — "Clonar voz" es un ítem más de la
                lista, con card del mismo tamaño. */}
            {loading ? (
              <div className="flex-1 flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 flex flex-col gap-2">
                {hasCloned ? (
                  <>
                    <p className={SECTION_TITLE_CLASS}>Mis voces</p>
                    {clonedVoices.map((voice) => {
                      const isSelected =
                        selectedVozId === voice.elevenlabsVoiceId;
                      return (
                        <button
                          key={voice.id}
                          type="button"
                          onClick={() =>
                            pick(voice.elevenlabsVoiceId, voice.nombre)
                          }
                          className={`w-full text-left rounded-2xl p-3.5 transition-colors ${
                            isSelected
                              ? 'bg-[#1644EB]/[0.06] ring-2 ring-brand-primary'
                              : 'bg-white ring-1 ring-black/[0.08] hover:bg-black/[0.02]'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Mic className="w-4 h-4 text-brand-primary shrink-0" />
                            <span className="text-[14px] font-medium text-brand-black truncate">
                              {voice.nombre}
                            </span>
                            {isSelected && (
                              <Check className="w-3.5 h-3.5 text-brand-primary shrink-0" />
                            )}
                          </div>
                          <p className="text-[12px] text-[#3C3C43]/50 mt-0.5">
                            Voz clonada
                          </p>
                        </button>
                      );
                    })}
                    {clonarVozButton}
                  </>
                ) : (
                  clonarVozButton
                )}
                {/* Catálogo — subtítulo siempre visible, filtros debajo. */}
                <p className={`${SECTION_TITLE_CLASS} mt-3`}>Voces de Atlas</p>
                {filterChips}
                {atlasRows}
              </div>
            )}
          </>
        )}
      </div>
    </>,
    document.body,
  );
};
